/**
 * Regras de apresentação dos valores do detalhamento da embarcação (AC4).
 *
 * Nenhuma delas calcula ou deriva informação: o valor recebido da fonte é
 * apenas formatado para leitura (RN005).
 */

/** Campo cujo valor chega `null` ou vazio: mostra apenas o rótulo, sem texto. */
export const EMPTY_FIELD_TEXT = "";

const CNPJ_LENGTH = 14;

/**
 * CNPJ com a máscara `00.000.000/0000-00` quando o valor é estritamente
 * numérico com os 14 dígitos do documento. Valor alfanumérico — previsto no
 * contrato — ou já formatado é apresentado exatamente como recebido.
 */
export function formatCnpj(value: string): string {
  if (value.length !== CNPJ_LENGTH || !/^\d+$/.test(value)) return value;

  return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12)}`;
}

/**
 * A data da última atualização chega sem hora; a leitura em UTC evita que o
 * fuso do navegador devolva o dia anterior.
 */
const DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

/** Data no formato da tela; o texto original segue quando a data não é legível. */
export function formatDetailDate(value: string): string {
  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? value : DATE_FORMATTER.format(timestamp);
}

/** Data do contrato: `aaaa-mm-dd`, com ou sem a hora. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}([T ].*)?$/;

/**
 * Campo declarado como data no contrato, mas que a fonte pode preencher com
 * outro texto (é o caso da Extensão do Contrato, guardada "conforme valor
 * disponível na base"). Só a data do contrato é reescrita no formato da tela;
 * qualquer outro valor vai exatamente como recebido — inclusive um número
 * solto, que `Date` leria como um ano ou um mês.
 */
export function formatDetailDateOrText(value: string): string {
  return ISO_DATE_PATTERN.test(value) ? formatDetailDate(value) : value;
}

/**
 * O teto de casas decimais do `Intl`: o valor sai com as casas que a fonte tem,
 * sem arredondamento nem casas acrescentadas.
 */
const MAX_FRACTION_DIGITS = 20;

const NUMBER_FORMATTER = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: MAX_FRACTION_DIGITS,
});

/**
 * Valor numérico no idioma da tela: vírgula decimal e ponto de milhar. A
 * unidade de medida não entra aqui — ela pertence ao rótulo do campo.
 */
export function formatDetailNumber(value: number): string {
  return NUMBER_FORMATTER.format(value);
}
